import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useAvatarCache } from '@/hooks/useAvatarCache';
import { setUser as setSentryUser, clearUser as clearSentryUser, addBreadcrumb } from '@/lib/sentry';
import { useLeaderboardPreload } from '@/hooks/useLeaderboardPreload';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isPremium: boolean;
  isLoadingAuth: boolean;
  isLoadingPremium: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isPremium: false,
  isLoadingAuth: true,
  isLoadingPremium: true,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  // Fetch and cache user profile (including avatar, premium status, and location)
  // IMPORTANT: Use unique queryKey to avoid cache collision with main profile query
  const { data: profile, isLoading: isLoadingPremium } = useQuery({
    queryKey: ["profile-avatar", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("avatar_url, user_timezone, is_premium, city, country")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Preload and cache avatar when profile loads
  useAvatarCache(user?.id || '', profile?.avatar_url);

  // Fetch blocked users for preloading leaderboards
  const { data: blockedUsers = [], isLoading: isLoadingBlocked } = useQuery({
    queryKey: ["blocked-users", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // Get users that current user has blocked
      const { data: usersIBlocked, error: error1 } = await supabase
        .from("blocked_users")
        .select("blocked_user_id")
        .eq("user_id", user.id);
      
      // Get users who have blocked current user
      const { data: usersWhoBlockedMe, error: error2 } = await supabase
        .from("blocked_users")
        .select("user_id")
        .eq("blocked_user_id", user.id);
      
      if (error1 || error2) throw error1 || error2;
      
      // Combine both lists - users I blocked and users who blocked me
      const blockedByMe = usersIBlocked?.map(b => b.blocked_user_id) || [];
      const blockedMe = usersWhoBlockedMe?.map(b => b.user_id) || [];
      const allBlockedUsers = [...new Set([...blockedByMe, ...blockedMe])];
      
      return allBlockedUsers;
    },
    enabled: !!user?.id,
  });

  // Fetch user's connections (followers + following) for preloading leaderboards
  const { data: userConnections = [], isLoading: isLoadingConnections } = useQuery({
    queryKey: ["user-connections", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // Get users that current user follows
      const { data: following, error: error1 } = await supabase
        .from("follows")
        .select("following_id")
        .eq("follower_id", user.id);
      
      // Get users who follow current user
      const { data: followers, error: error2 } = await supabase
        .from("follows")
        .select("follower_id")
        .eq("following_id", user.id);
      
      if (error1 || error2) throw error1 || error2;
      
      // Combine both lists - users I follow and users who follow me
      const followingIds = following?.map(f => f.following_id) || [];
      const followerIds = followers?.map(f => f.follower_id) || [];
      const allConnections = [...new Set([...followingIds, ...followerIds])];
      
      return allConnections;
    },
    enabled: !!user?.id,
  });

  // Preload leaderboard data for all filters when user authenticates
  useLeaderboardPreload({
    userId: user?.id || null,
    userCity: profile?.city || null,
    userCountry: profile?.country || null,
    isPremium: profile?.is_premium ?? false,
    blockedUsers,
    userConnections,
    isLoadingBlocked,
    isLoadingConnections,
  });

  // Auto-update timezone when user opens app (if timezone has changed)
  useEffect(() => {
    const updateTimezone = async () => {
      if (!user?.id || !profile) return;
      
      const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Only update if timezone has changed
      if (profile.user_timezone !== currentTimezone) {
        console.log(`[Auth] Timezone changed from ${profile.user_timezone} to ${currentTimezone}, updating...`);
        
        const { error } = await supabase
          .from("profiles_internal")
          .update({ user_timezone: currentTimezone })
          .eq("id", user.id);
        
        if (error) {
          console.error('[Auth] Failed to update timezone:', error);
        } else {
          console.log('[Auth] Timezone updated successfully');
        }
      }
    };
    
    updateTimezone();
  }, [user?.id, profile]);

  // Get premium status from profile
  const isPremium = profile?.is_premium ?? false;

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[Auth] Auth state changed:', event);
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoadingAuth(false);
        
        // Update Sentry user context
        if (session?.user) {
          setSentryUser(
            session.user.id,
            session.user.email,
            session.user.user_metadata?.username
          );
          addBreadcrumb('User signed in', 'auth', {
            userId: session.user.id,
            event,
          });
        } else {
          clearSentryUser();
          addBreadcrumb('User signed out', 'auth', { event });
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoadingAuth(false);
    });

    // Handle app coming back from background - refresh session
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[Auth] App returned to foreground, checking session...');
        try {
          // Get current session first
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          
          // Only try to refresh if there's an active session
          if (currentSession) {
            console.log('[Auth] Refreshing existing session...');
            const { data: { session }, error } = await supabase.auth.refreshSession();
            if (error) {
              console.error('[Auth] Failed to refresh session:', error);
              setSession(currentSession);
              setUser(currentSession?.user ?? null);
            } else {
              console.log('[Auth] Session refreshed successfully');
              setSession(session);
              setUser(session?.user ?? null);
            }
          } else {
            console.log('[Auth] No session to refresh');
            setSession(null);
            setUser(null);
          }
        } catch (error) {
          console.error('[Auth] Error handling session:', error);
        }
      }
    };

    // Periodic session refresh - keep session alive even when app is idle
    // Refresh every 30 minutes to ensure token stays fresh
    const refreshInterval = setInterval(async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession) {
        console.log('[Auth] Periodic session refresh (every 30min)...');
        const { data: { session }, error } = await supabase.auth.refreshSession();
        if (error) {
          console.error('[Auth] Periodic refresh failed:', error);
        } else {
          console.log('[Auth] Periodic refresh successful');
          setSession(session);
          setUser(session?.user ?? null);
        }
      }
    }, 30 * 60 * 1000); // 30 minutes

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isPremium,
        isLoadingAuth,
        isLoadingPremium,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
